import TableLanguagesCell from '@/contexts/shared/components/table/TableLanguagesCell'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ViewIcon from '@mui/icons-material/Visibility'
import { Box } from '@mui/material'
import {
  DataGrid,
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridRowParams,
  GridRowSelectionModel,
  GridRowsProp
} from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { assignHighlight, showResource } from '../../../../../redux/actions/resources'
import { IRootState } from '../../../../../redux/types'
import CellTitleValue from '../../../../shared/components/CellTitleValue'
import HighlightIcon from '../../../../shared/components/icons/HighlightIcon'
import VisibilityIcon from '../../../../shared/components/icons/VisibilityIcon'
import TagInput from '../../../../shared/components/inputs/TagInput'
import CustomToolbar from '../../../../shared/components/table/CustomToolbar'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import { ResourceListApi } from '../../models/resource'
import DialogConfirmationDelete from './DialogConfirmationDelete'
import FiletypeIcon from './FiletypeIcon'
import UpdateTitleModal from './UpdateTitleModal'
import UpdateTitleSnackbar from './UpdateTitleSnackbar'

function ResourceTable({ rowsSelected, setRowsSelected }: any) {
  const navigate = useNavigate()
  const isMobile = useScreenWidth(700)
  const dispatch = useDispatch()
  const { t } = useTranslation(['global'])

  const { globalBrandId } = useSelector((state: IRootState) => state.global)
  const { resources, aliasUpdated } = useSelector((state: IRootState) => state.resource)
  const [openDeleteResource, setOpenDeleteResource] = useState(false)
  const [resourceSelected, setResourceSelected] = useState(0)
  const [openUpdateTitleModal, setOpenUpdateTitleModal] = useState(false)
  const [selectedResourceForTitle, setSelectedResourceForTitle] = useState<any | null>(null)

  const [errors, setErrors] = useState<(string | null)[]>([null])
  const [msgs, setMsgs] = useState<(string | null)[]>([null])
  const [openSnackbar, setOpenSnackbar] = useState(false)

  useEffect(() => {
    try {
      if (aliasUpdated) {
        setMsgs([t('success-updating-title')])
        setOpenSnackbar(true)
      }
    } catch (err) {
      console.error(err)
      setErrors([t('error-updating-title')])
    }
  }, [aliasUpdated])

  const handleDownload = (resource_id: number) => {
    const URL = `${import.meta.env.VITE_API_URL}/resources/download/`
    window.open(URL + resource_id)
  }

  const handleClickHighlight = (id: number, isHighlight: boolean) => {
    dispatch(assignHighlight({ id, isHighlight }))
  }

  const handleOpenUpdateTitleModal = (resource: any) => {
    setSelectedResourceForTitle(resource)
    setOpenUpdateTitleModal(true)
  }

  const initialState = {
    columns: {
      columnVisibilityModel: {
        date: !isMobile,
        role: !isMobile,
        languages_codes: !isMobile,
        brand_name: globalBrandId === null,
        filetype: !isMobile && globalBrandId === null,
        tags: !isMobile
      }
    }
  }
  const rows: GridRowsProp<ResourceListApi> = resources ?? []
  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: t('name'),
      flex: 1,
      renderCell: (params: any) => <CellTitleValue value={params.value} onClick={() => handleOpenUpdateTitleModal(params.row)} />
    },
    {
      field: 'highlight',
      headerName: ' ',
      width: 80,
      renderCell: (params: any) => (
        <Box display="flex" alignItems="center" gap={1}>
          <HighlightIcon id={params.id} isHighlight={params.value} handleClick={handleClickHighlight} />
          <VisibilityIcon id={params.row.visibility} />
        </Box>
      )
    },
    {
      field: 'tags',
      headerName: ' ',
      width: 120,
      renderCell: (params: any) => <TagInput rowId={params.id} rowTags={params.value} rowBrandId={params.row.brand_id} page={'resources'} />
    },
    {
      field: 'filetype',
      headerName: t('type'),
      width: 80,
      renderCell: (params: any) => <FiletypeIcon id={params.row.filetype_id} />
    },
    { field: 'brand_name', headerName: t('brand'), width: 100 },
    {
      field: 'language_codes',
      headerName: t('language'),
      width: 100,
      renderCell: (params: GridCellParams) => {
        return <TableLanguagesCell params={params} />
      }
    },
    { field: 'role', headerName: t('rol'), width: 120 },
    {
      field: 'date',
      headerName: t('upload-date'),
      width: 100,
      renderCell: (params: GridCellParams) => {
        if (params.row.date != null) {
          return dayjs(params.row.date).format('DD MMM YYYY')
        }
        return '--'
      }
    },
    {
      field: 'actions',
      type: 'actions',
      width: 30,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={params.id}
          icon={<ViewIcon />}
          label={t('view')}
          onClick={() => navigate(`/admin/biblioteca/${params.id}`)}
          showInMenu
        />,
        <GridActionsCellItem
          key={params.id}
          icon={<EditIcon />}
          label={t('edit')}
          onClick={() => {
            dispatch(showResource(params.id))
          }}
          showInMenu
        />,
        <GridActionsCellItem
          key={params.id}
          icon={<CloudDownloadIcon />}
          label={t('download')}
          onClick={() => handleDownload(Number(params.id))}
          showInMenu
        />,
        <GridActionsCellItem
          key={params.id}
          icon={<DeleteIcon />}
          label={t('delete')}
          onClick={() => {
            setOpenDeleteResource(!openDeleteResource)
            setResourceSelected(Number(params.id))
          }}
          showInMenu
        />
      ]
    }
  ]

  return (
    <>
      <div className="table_custom">
        <div style={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            disableColumnMenu
            checkboxSelection
            onRowSelectionModelChange={(newSelectionModel: GridRowSelectionModel) => {
              setRowsSelected(newSelectionModel as number[])
            }}
            rowSelectionModel={rowsSelected}
            disableRowSelectionOnClick
            initialState={initialState}
            slots={{ toolbar: CustomToolbar }}
            slotProps={{
              toolbar: {
                quickFilterProps: { debounceMs: 500 },
                selectedRows: rowsSelected.length
              }
            }}
          />
        </div>
      </div>

      {openUpdateTitleModal && (
        <UpdateTitleModal open={openUpdateTitleModal} resource={selectedResourceForTitle} onClose={() => setOpenUpdateTitleModal(false)} />
      )}
      {openSnackbar && msgs.length > 0 && <UpdateTitleSnackbar msgs={msgs} errors={errors} />}

      <DialogConfirmationDelete
        openDeleteResource={openDeleteResource}
        setOpenDeleteResource={setOpenDeleteResource}
        resourceSelected={resourceSelected}
      />
    </>
  )
}

export default ResourceTable
